import { yupResolver } from '@hookform/resolvers/yup'
import type { BaseSyntheticEvent } from 'react'
import { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import { Button } from '@src/components/Button/Button'
import { ModalContent } from '@src/components/Modal/ModalContent'
import { ChangePassword } from '@src/components/SignUp/ChangePassword'
import { Toast } from '@src/components/Toaster/Toast'
import type { PasswordSchema } from '@src/constants/AccountSchema'
import { passwordSchema } from '@src/constants/AccountSchema'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ServerActions } from '@src/services/server-actions.service'

interface Props {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  loading: boolean
  setLoading: (loading: boolean) => void
}

export function ChangePasswordForm({ isOpen, setIsOpen, loading, setLoading }: Props) {
  const { t } = useLocale('accountPage')
  const { t: tspf } = useLocale('set-password-form')

  const passwordForm = useForm<PasswordSchema>({
    mode: 'onTouched',
    disabled: loading,
    resolver: yupResolver(passwordSchema),
  })

  useEffect(() => {
    if (isOpen) return
    passwordForm.reset()
  }, [passwordForm, isOpen])

  const onPasswordFormSubmit = useCallback(
    async (data: PasswordSchema, event?: BaseSyntheticEvent) => {
      try {
        event?.preventDefault()
        if (loading) return
        setLoading(true)
        await ServerActions.serial.changePassword({
          oldPassword: data.currentPassword,
          newPassword: data.newPassword,
        })
        setLoading(false)
        setIsOpen(false)
        toast(() => <Toast message={t('passwordUpdated')} variant="success" />)
      } catch (error: any) {
        setLoading(false)
        if (error?.message?.includes('incorrect_password')) {
          return passwordForm.setError('currentPassword', {
            type: 'manual',
            message: 'password.incorrect',
          })
        }
        setIsOpen(false)
        toast(() => <Toast message={t('error')} variant="error" />)
      }
    },
    [loading, passwordForm, setIsOpen, setLoading, t]
  )

  return (
    <ModalContent
      size="medium"
      position="center"
      title={t('editPassword')}
      isOpen={isOpen}
      onClose={() => {
        if (loading) return
        setIsOpen(false)
      }}
    >
      <form onSubmit={passwordForm.handleSubmit(onPasswordFormSubmit)}>
        <FormProvider {...passwordForm}>
          <ChangePassword
            maxLength={20}
            disabled={loading}
            ruleText={{
              ruleMinEightMaxTwentyChars: tspf('ruleMinEightMaxTwentyChars'),
              ruleOneUpper: tspf('ruleOneUpper'),
              ruleOneLower: tspf('ruleOneLower'),
              ruleOneNumber: tspf('ruleOneNumber'),
              ruleNoBannedChars: tspf('ruleNoBannedChars'),
            }}
            onKeyDown={(e) => e.key === 'Enter'}
          />
          <div className="mt-6 flex flex-col gap-2 lg:mt-8 lg:flex-row">
            <Button onClick={() => {}} size="large" isLoading={loading}>
              {t('update')}
            </Button>
            <Button
              onClick={() => {
                if (loading) return
                setIsOpen(false)
              }}
              size="large"
              variant="secondary"
              disabled={loading}
            >
              {t('cancel')}
            </Button>
          </div>
        </FormProvider>
      </form>
    </ModalContent>
  )
}
